/* eslint-disable @typescript-eslint/no-empty-function */
import { BaggingData } from "@/domain/entities/Bagging";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import QRCode from "qrcode";
import "@/app/ui/assets/fonts/jsPDF/Poppins/Poppins-Regular";
import "@/app/ui/assets/fonts/jsPDF/Poppins/Poppins-Bold";
import "@/app/ui/assets/fonts/jsPDF/Poppins/Poppins-SemiBold";
import en from "@/i18n/locales/en";
import id from "@/i18n/locales/id";
import my from "@/i18n/locales/my";
import { createI18n } from "vue-i18n";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";

// Constants
const PAGE_WIDTH = 74;
const PAGE_HEIGHT = 105;
const MARGIN = 2;
const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN * 2);
const Y_HEADER_SEP = 42;
const Y_INFO_SEP = 60;

interface LabelContext {
  data: BaggingData;
  qrcodeValue: string;
  logo: string;
  barcode: string;
}

let translate: any = () => {};

const countryCode = AccountController.accountData?.countryCode ?? "ID";

const initI18n = () => {
  const i18nInstance = createI18n({
    legacy: false,
    locale: AccountController.accountData.account_type_detail.countryCode.toLowerCase(),
    fallbackLocale: "id",
    globalInjection: false,
    messages: { en, id, my }
  });
  translate = i18nInstance.global.t;
};

const generateQRCode = async (text: string): Promise<string> => {
  return QRCode.toDataURL(text);
};

const drawLines = (doc: jsPDF) => {
  doc.setLineWidth(0.5);
  doc.setFillColor("#000000");
  doc.line(0, Y_HEADER_SEP, PAGE_WIDTH, Y_HEADER_SEP);
  doc.line(0, Y_INFO_SEP, PAGE_WIDTH, Y_INFO_SEP);
};

const drawTopSection = (doc: jsPDF, ctx: LabelContext) => {
  const { qrcodeValue, logo, data } = ctx;
  // QR Code (Left)
  doc.addImage(qrcodeValue, "PNG", MARGIN - 2, MARGIN - 2, 40, 40, "qrcode", "FAST", 0);

  // Logo
  const rightBlockX = 40;
  doc.addImage(logo, "PNG", rightBlockX + 6, MARGIN + 3, 25, 6, "logo", "FAST", 0);

  const rightAlignX = PAGE_WIDTH - MARGIN - 2;

  // "Tujuan" Label
  doc.setFont("Poppins", "normal");
  doc.setFontSize(10);
  doc.setTextColor("#333333");
  doc.text(translate('printReceipt.destination'), rightAlignX, MARGIN + 15, { align: "right" });

  // Destination City Code
  doc.setFont("Poppins", "bold");
  doc.setFontSize(40);
  doc.setTextColor("#333333");
  doc.text(data.bagDestinationCityCode, rightAlignX + 1, MARGIN + 30, { align: "right" });
};

const drawMiddleSection = (doc: jsPDF, ctx: LabelContext) => {
  const { data } = ctx;
  const yLabel = Y_HEADER_SEP + 5;
  const yValue = Y_HEADER_SEP + 11;

  // Column 1: CW
  doc.setFont("Poppins", "normal");
  doc.setFontSize(7);
  doc.text("CW", MARGIN + 2, yLabel);
  
  doc.setFont("Poppins", "bold");
  doc.setFontSize(12);
  const cw = data.isUseNewCalculation ? data.bagTotalChargeableWeight : (data.bagCustomGrossWeight > data.bagCustomVolumeWeight ? data.bagCustomGrossWeight : data.bagCustomVolumeWeight);
  doc.text(`${formatByCountry(cw, { type: 'weight', country: countryCode })} kg`, MARGIN + 2, yValue);

  // Column 2: Berat Kotor/volume
  const midColX = PAGE_WIDTH / 2;
  doc.setFont("Poppins", "normal");
  doc.setFontSize(7);
  doc.text(translate('printReceipt.grossVolumeWeightKg'), midColX, yLabel, { align: "center" });
  
  doc.setFont("Poppins", "semibold");
  doc.setFontSize(12);
  const grossVolText = `${formatByCountry(data.bagCustomGrossWeight, { type: 'weight', country: countryCode })} kg/${formatByCountry(data.bagCustomVolumeWeight, { type: 'weight', country: countryCode })} kg`;
  doc.text(grossVolText, midColX, yValue, { align: "center" });

  // Column 3: STT/Koli
  doc.setFont("Poppins", "normal");
  doc.setFontSize(7);
  doc.text(translate('printReceipt.sttKoli'), PAGE_WIDTH - (MARGIN + 2), yLabel, { align: "right" });
  
  doc.setFont("Poppins", "semibold");
  doc.setFontSize(12);
  const sttKoliText = `${data.bagTotalStt}/${data.bagTotalPiece}`;
  doc.text(sttKoliText, PAGE_WIDTH - MARGIN - 2, yValue, { align: "right" });
};

const drawBottomSection = (doc: jsPDF, ctx: LabelContext) => {
  const { data, barcode } = ctx;
  // Bag Number
  doc.setFont("Poppins", "bold");
  doc.setFontSize(14);
  const bagNo = data.bagCode;
  doc.text(bagNo, PAGE_WIDTH / 2, Y_INFO_SEP + 8, { align: "center" });

  // Barcode
  doc.addImage(barcode, "PNG", MARGIN + 2, Y_INFO_SEP + 12, CONTENT_WIDTH - 4, 20, "barcode", "FAST", 0);

  // Footer Text
  doc.setFont("Poppins", "bold");
  doc.setFontSize(11);
  doc.text(data.bagPartnerName, PAGE_WIDTH / 2, PAGE_HEIGHT - MARGIN - 3, { align: "center" });
};

const labelPdfA7 = async (data: BaggingData, bagVendorNo: string, logo: string, barcode: string) => {
  initI18n();
  const doc: any = new jsPDF("p", "mm", "a7", true);
  
  const combineQRText = `${bagVendorNo || data.bagCode}#${data.bagOriginCityCode}#${
    data.bagDestinationCityCode
  }#${data.bagTotalChargeableWeight}#${formatByCountry(data.bagCustomGrossWeight, {type: 'weight', country: countryCode})}#${
    formatByCountry(data.bagCustomVolumeWeight, {type: 'weight', country: countryCode })
  }#${data.bagCreatedAt.slice(0, 10)}`;
  const qrcodeValue = await generateQRCode(combineQRText);

  const ctx: LabelContext = {
    data,
    qrcodeValue,
    logo,
    barcode
  };

  drawLines(doc);
  drawTopSection(doc, ctx);
  drawMiddleSection(doc, ctx);
  drawBottomSection(doc, ctx);

  doc.setProperties({ title: "Bagging Manifest" });
  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
};

export default labelPdfA7;
